<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';

import FinancialAPI from 'dashboard/api/financial';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';

const copy = {
  title: 'Financeiro',
  description:
    'Consulte sua assinatura e pague suas cobranças sem sair do Talkvia.',
  loading: 'Carregando dados financeiros...',
  empty: 'Nenhuma assinatura vinculada a esta conta.',
  errorTitle: 'Não foi possível carregar os dados financeiros.',
  subscription: 'Assinatura',
  invoices: 'Cobranças da assinatura',
  invoiceCount: 'cobranças',
  value: 'Valor',
  billingType: 'Forma',
  cycle: 'Ciclo',
  nextDueDate: 'Próximo vencimento',
  status: 'Status',
  companyAccess: 'Vencimento da assinatura',
  companyAccessDescription:
    'Consulte até qual data a assinatura da empresa está válida.',
  accessExpiresAt: 'Data de vencimento',
  noExpiry: 'Sem vencimento informado',
  payment: 'Cobrança',
  dueDate: 'Vencimento',
  paymentDate: 'Pagamento',
  links: 'Pagamento',
  invoice: 'Pagar no Talkvia',
  paid: 'Concluído',
  noPayments: 'Nenhuma cobrança encontrada para esta assinatura.',
  missingCompany: 'Não existe vínculo financeiro para esta conta.',
  missingCompanyDescription:
    'Assim que a assinatura for vinculada, o plano, os vencimentos e as cobranças aparecerão automaticamente aqui.',
  overview: 'Visão geral financeira',
  overviewDescription:
    'Acompanhe assinatura, vencimentos e cobranças da sua empresa em um só lugar.',
  linked: 'Financeiro conectado',
  pendingSetup: 'Configuração pendente',
  noSubscriptionShort: 'Não vinculada',
  noChargeValue: 'Sem cobrança',
  paidPayments: 'Pagas',
  openPayments: 'Em aberto',
  overduePayments: 'Vencidas',
  outstandingValue: 'Total em aberto',
  subscriptionDetails: 'Detalhes da assinatura',
  subscriptionDetailsDescription:
    'Informações do plano e da recorrência da sua conta Talkvia.',
  subscriptionId: 'Referência da assinatura',
  futurePaymentsHint: 'As cobranças futuras serão listadas nesta área.',
  oneOffPurchases: 'Compras avulsas',
  oneOffPurchasesDescription:
    'Créditos comprados separadamente da sua assinatura.',
  oneOffPurchaseCount: 'compras',
  oneOffProduct: 'Produto',
  credits: 'Créditos',
  packages: 'pacote(s)',
  purchaseDate: 'Compra',
  notAvailable: '-',
  noOneOffPurchases: 'Nenhuma compra avulsa encontrada.',
  noOneOffPurchasesHint:
    'As compras de créditos feitas no Marketplace aparecerão nesta área.',
  cancelSubscription: 'Cancelar assinatura',
  cancelTitle: 'Cancelar assinatura',
  cancelDescription:
    'Esta ação é irreversível. Ao cancelar, sua assinatura será encerrada e o acesso poderá ser suspenso.',
  cancelReasonLabel: 'Motivo do cancelamento',
  cancelReasonOther: 'Outros',
  cancelReasonPlaceholder: 'Descreva o motivo do cancelamento...',
  cancelConfirm: 'Confirmar cancelamento',
  cancelDismiss: 'Voltar',
  cancelling: 'Cancelando...',
  cancelSuccess: 'Assinatura cancelada com sucesso.',
  cancelError: 'Não foi possível cancelar a assinatura.',
  trialCancellationTitle: 'Cancelamento da conta de teste',
  trialCancellationDescription:
    'Você está dentro dos 7 dias de teste e pode cancelar a conta imediatamente. Se houver uma assinatura vinculada, ela também será cancelada.',
  trialEndsAt: 'Período de teste disponível até:',
  cancelAccount: 'Cancelar conta',
  cancelAccountTitle: 'Cancelar conta de teste',
  cancelAccountDescription:
    'Esta ação desativará sua conta imediatamente. A assinatura vinculada também será cancelada e você perderá o acesso ao sistema.',
  cancelAccountConfirm: 'Confirmar e cancelar conta',
  cancelAccountSuccess: 'Conta cancelada com sucesso.',
  checkoutTitle: 'Pagar cobrança',
  checkoutDescription:
    'Escolha Pix ou cartão de crédito para concluir no Talkvia.',
  pix: 'Pix',
  card: 'Cartão de crédito',
  pixLoading: 'Gerando Pix...',
  pixCopyPaste: 'Pix Copia e Cola',
  copyPix: 'Copiar código Pix',
  pixCopied: 'Código Pix copiado.',
  pixExpires: 'Validade do código',
  cardHolderName: 'Nome impresso no cartão',
  cardNumber: 'Número do cartão',
  cardExpiryMonth: 'Mês',
  cardExpiryYear: 'Ano',
  cardCcv: 'CVV',
  holderName: 'Nome do titular',
  holderDocument: 'CPF ou CNPJ',
  holderEmail: 'E-mail do titular',
  holderPostalCode: 'CEP',
  holderAddressNumber: 'Número',
  holderAddressComplement: 'Complemento',
  holderPhone: 'Telefone',
  useForSubscription: 'Usar este cartão nas próximas cobranças',
  cardSecurity:
    'Os dados são enviados de forma segura ao processador e não são armazenados pelo Talkvia.',
  payCard: 'Pagar com cartão',
  processingCard: 'Processando pagamento...',
  paymentSuccess: 'Pagamento enviado com sucesso.',
  subscriptionUpdated: 'A assinatura foi alterada para cartão de crédito.',
  checkoutError: 'Não foi possível concluir o pagamento.',
  close: 'Fechar',
};

const CANCEL_REASONS = [
  'Não utilizo mais o serviço',
  'O custo está alto para mim',
  'Encontrei uma alternativa melhor',
  'Problemas técnicos frequentes',
  'Atendimento ao suporte insatisfatório',
  'Vou fazer uma pausa temporária',
  copy.cancelReasonOther,
];

const paymentStatusLabels = {
  PAID: 'Pago',
  RECEIVED: 'Pago',
  CONFIRMED: 'Confirmado',
  RECEIVED_IN_CASH: 'Recebido em dinheiro',
  PENDING: 'Pendente',
  OVERDUE: 'Vencido',
  REFUNDED: 'Estornado',
  CANCELLED: 'Cancelado',
};

const subscriptionStatusLabels = {
  ACTIVE: 'Ativa',
  INACTIVE: 'Inativa',
  EXPIRED: 'Expirada',
  OVERDUE: 'Em atraso',
  CANCELLED: 'Cancelada',
};

const cycleLabels = {
  WEEKLY: 'Semanal',
  BIWEEKLY: 'Quinzenal',
  MONTHLY: 'Mensal',
  BIMONTHLY: 'Bimestral',
  QUARTERLY: 'Trimestral',
  SEMIANNUALLY: 'Semestral',
  YEARLY: 'Anual',
};

const billingTypeLabels = {
  UNDEFINED: 'Indefinido',
  BOLETO: 'Boleto',
  CREDIT_CARD: 'Cartão de crédito',
  PIX: 'Pix',
};

const isLoading = ref(false);
const route = useRoute();
const error = ref('');
const financial = ref({});

const showCancelModal = ref(false);
const selectedReason = ref('');
const otherReason = ref('');
const isCancelling = ref(false);
const cancelError = ref('');
const cancelSuccess = ref(false);
const cancelSuccessMessage = ref('');
const cancelMode = ref('subscription');
const showPaymentModal = ref(false);
const selectedPayment = ref(null);
const paymentMethod = ref('pix');
const pixData = ref(null);
const isLoadingPix = ref(false);
const isPayingCard = ref(false);
const checkoutError = ref('');
const checkoutSuccess = ref('');
const checkoutWarning = ref('');
const cardForm = reactive({
  holder_name: '',
  card_number: '',
  expiry_month: '',
  expiry_year: '',
  ccv: '',
  name: '',
  email: '',
  cpf_cnpj: '',
  postal_code: '',
  address_number: '',
  address_complement: '',
  phone: '',
  use_for_subscription: true,
});

const subscription = computed(() => financial.value.subscription || {});
const companyRecord = computed(() => financial.value.company_record || null);
const payments = computed(
  () => financial.value.payments || financial.value.paid_payments || []
);
const oneOffPurchases = computed(() => financial.value.one_off_purchases || []);
const hasSubscription = computed(() => Object.keys(subscription.value).length);
const hasFinancialLink = computed(() =>
  Boolean(companyRecord.value || financial.value.subscription_id)
);
const isSubscriptionActive = computed(() =>
  ['ACTIVE', 'INACTIVE'].includes(subscription.value.status)
);
const withinTrialPeriod = computed(
  () => financial.value.within_trial_period === true
);
const isAccountCancellation = computed(() => cancelMode.value === 'account');
const cancelModalTitle = computed(() =>
  isAccountCancellation.value ? copy.cancelAccountTitle : copy.cancelTitle
);
const cancelModalDescription = computed(() =>
  isAccountCancellation.value
    ? copy.cancelAccountDescription
    : copy.cancelDescription
);
const cancelConfirmLabel = computed(() =>
  isAccountCancellation.value ? copy.cancelAccountConfirm : copy.cancelConfirm
);
const currentAccessExpiry = computed(
  () => financial.value.account_access_expires_at || ''
);
const paidPayments = computed(() =>
  payments.value.filter(payment =>
    ['RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH'].includes(payment.status)
  )
);
const overduePayments = computed(() =>
  payments.value.filter(payment => payment.status === 'OVERDUE')
);
const openPayments = computed(() =>
  payments.value.filter(payment =>
    ['PENDING', 'OVERDUE'].includes(payment.status)
  )
);
const openOneOffPurchases = computed(() =>
  oneOffPurchases.value.filter(purchase =>
    ['pending', 'overdue'].includes(purchase.status)
  )
);
const outstandingValue = computed(() =>
  openPayments.value.reduce(
    (total, payment) => total + Number(payment.value || 0),
    0
  )
);
const payer = computed(() => financial.value.payer || {});
const selectedPaymentIsOpen = computed(() =>
  ['PENDING', 'OVERDUE'].includes(selectedPayment.value?.status)
);

const isOtherReason = computed(
  () => selectedReason.value === copy.cancelReasonOther
);

const cancelReasonText = computed(() => {
  if (!selectedReason.value) return '';
  if (isOtherReason.value) return otherReason.value.trim();
  return selectedReason.value;
});

const canConfirmCancel = computed(() => {
  if (!selectedReason.value) return false;
  if (isOtherReason.value) return otherReason.value.trim().length > 0;
  return true;
});

const formatCurrency = value => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value || 0));
};

const formatDateTime = value => {
  if (!value) return '-';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};

const formatDate = value => {
  if (!value) return '-';

  const normalizedValue = /^\d{4}-\d{2}-\d{2}$/.test(String(value))
    ? `${value}T00:00:00`
    : value;
  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(date);
};

const statusLabel = status => paymentStatusLabels[status] || status || '-';
const subscriptionStatusLabel = status =>
  subscriptionStatusLabels[status] || status || copy.noSubscriptionShort;
const paymentStatusClass = status => {
  if (['PAID', 'RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH'].includes(status)) {
    return 'border-n-teal-7 bg-n-teal-3 text-n-teal-11';
  }
  if (status === 'OVERDUE') {
    return 'border-n-ruby-7 bg-n-ruby-3 text-n-ruby-11';
  }
  if (status === 'PENDING') {
    return 'border-n-amber-7 bg-n-amber-3 text-n-amber-11';
  }
  return 'border-n-weak bg-n-alpha-2 text-n-slate-11';
};
const subscriptionStatusClass = computed(() => {
  if (subscription.value.status === 'ACTIVE') {
    return 'border-n-teal-7 bg-n-teal-3 text-n-teal-11';
  }
  if (['OVERDUE', 'EXPIRED'].includes(subscription.value.status)) {
    return 'border-n-ruby-7 bg-n-ruby-3 text-n-ruby-11';
  }
  return 'border-n-weak bg-n-alpha-2 text-n-slate-11';
});
const cycleLabel = cycle => cycleLabels[cycle] || cycle || '-';
const billingTypeLabel = billingType =>
  billingTypeLabels[billingType] || billingType || '-';

const fetchFinancial = async () => {
  isLoading.value = true;
  error.value = '';

  try {
    const { data } = await FinancialAPI.get();
    financial.value = data;
    error.value = data.financial_error || '';
  } catch (exception) {
    error.value =
      exception.response?.data?.error ||
      exception.response?.data?.message ||
      copy.errorTitle;
  } finally {
    isLoading.value = false;
  }
};

const openCancelModal = (mode = 'subscription') => {
  cancelMode.value = mode;
  selectedReason.value = '';
  otherReason.value = '';
  cancelError.value = '';
  cancelSuccess.value = false;
  cancelSuccessMessage.value = '';
  showCancelModal.value = true;
};

const closeCancelModal = () => {
  showCancelModal.value = false;
};

const confirmCancel = async () => {
  if (!canConfirmCancel.value) return;
  isCancelling.value = true;
  cancelError.value = '';

  try {
    const { data } = await FinancialAPI.cancel(cancelReasonText.value, {
      cancelAccount: isAccountCancellation.value,
    });
    cancelSuccess.value = true;
    cancelSuccessMessage.value =
      data?.message ||
      (isAccountCancellation.value
        ? copy.cancelAccountSuccess
        : copy.cancelSuccess);

    if (data?.account_cancelled) {
      setTimeout(() => {
        window.location.assign(
          `/app/accounts/${route.params.accountId}/suspended`
        );
      }, 1800);
      return;
    }

    await fetchFinancial();
    setTimeout(() => {
      showCancelModal.value = false;
      cancelSuccess.value = false;
    }, 2000);
  } catch (exception) {
    cancelError.value =
      exception.response?.data?.error ||
      exception.response?.data?.message ||
      copy.cancelError;
  } finally {
    isCancelling.value = false;
  }
};

const resetCardForm = () => {
  Object.assign(cardForm, {
    holder_name: '',
    card_number: '',
    expiry_month: '',
    expiry_year: '',
    ccv: '',
    name: payer.value.name || '',
    email: payer.value.email || '',
    cpf_cnpj: payer.value.cpf_cnpj || '',
    postal_code: payer.value.postal_code || '',
    address_number: payer.value.address_number || '',
    address_complement: payer.value.address_complement || '',
    phone: payer.value.phone || '',
    use_for_subscription: !selectedPayment.value?.oneOff,
  });
};

const loadPix = async () => {
  if (!selectedPayment.value?.id) return;

  isLoadingPix.value = true;
  checkoutError.value = '';
  pixData.value = null;
  try {
    const { data } = await FinancialAPI.pix(selectedPayment.value.id);
    pixData.value = data;
  } catch (exception) {
    checkoutError.value =
      exception.response?.data?.error ||
      exception.response?.data?.message ||
      copy.checkoutError;
  } finally {
    isLoadingPix.value = false;
  }
};

const selectPaymentMethod = method => {
  paymentMethod.value = method;
  checkoutError.value = '';
  if (method === 'pix' && !pixData.value) loadPix();
};

const openPaymentModal = async payment => {
  selectedPayment.value = payment;
  paymentMethod.value = 'pix';
  checkoutError.value = '';
  checkoutSuccess.value = '';
  checkoutWarning.value = '';
  pixData.value = null;
  resetCardForm();
  showPaymentModal.value = true;
  await loadPix();
};

const openOneOffPaymentModal = purchase => {
  if (!purchase.payment_id) return;

  openPaymentModal({
    id: purchase.payment_id,
    status: purchase.status.toUpperCase(),
    value: purchase.amount,
    description: `${copy.oneOffPurchases} · ${purchase.product_name}`,
    oneOff: true,
  });
};

const closePaymentModal = () => {
  if (isPayingCard.value) return;
  showPaymentModal.value = false;
  selectedPayment.value = null;
  pixData.value = null;
  cardForm.card_number = '';
  cardForm.ccv = '';
};

const copyPixCode = async () => {
  if (!pixData.value?.payload) return;

  try {
    await navigator.clipboard.writeText(pixData.value.payload);
    checkoutSuccess.value = copy.pixCopied;
  } catch {
    checkoutError.value =
      'Não foi possível copiar automaticamente. Selecione e copie o código Pix.';
  }
};

const payWithCard = async () => {
  if (!selectedPayment.value?.id) return;

  isPayingCard.value = true;
  checkoutError.value = '';
  checkoutSuccess.value = '';
  checkoutWarning.value = '';
  try {
    const { data } = await FinancialAPI.payWithCreditCard(
      selectedPayment.value.id,
      { ...cardForm }
    );
    checkoutSuccess.value = data.subscription_updated
      ? `${copy.paymentSuccess} ${copy.subscriptionUpdated}`
      : copy.paymentSuccess;
    checkoutWarning.value = data.warning || '';
    cardForm.card_number = '';
    cardForm.ccv = '';
    await fetchFinancial();
    selectedPayment.value = selectedPayment.value?.oneOff
      ? {
          ...selectedPayment.value,
          status: (
            oneOffPurchases.value.find(
              item => item.payment_id === selectedPayment.value?.id
            )?.status || selectedPayment.value.status
          ).toUpperCase(),
        }
      : payments.value.find(item => item.id === selectedPayment.value?.id) ||
        selectedPayment.value;
  } catch (exception) {
    checkoutError.value =
      exception.response?.data?.error ||
      exception.response?.data?.message ||
      copy.checkoutError;
  } finally {
    isPayingCard.value = false;
  }
};

onMounted(async () => {
  await fetchFinancial();
  const requestedPayment =
    payments.value.find(payment => payment.id === route.query.payment) ||
    oneOffPurchases.value.find(
      purchase => purchase.payment_id === route.query.payment
    );
  if (
    requestedPayment &&
    ['PENDING', 'OVERDUE'].includes(requestedPayment.status.toUpperCase())
  ) {
    if (requestedPayment.payment_id) {
      openOneOffPaymentModal(requestedPayment);
    } else {
      openPaymentModal(requestedPayment);
    }
  }
});
</script>

<template>
  <SettingsLayout :is-loading="isLoading" :loading-message="copy.loading">
    <template #header>
      <BaseSettingsHeader :title="copy.title" :description="copy.description" />
    </template>

    <template #body>
      <section class="grid gap-5 pb-8">
        <div
          v-if="error"
          class="flex items-start gap-3 rounded-xl border border-n-amber-7 bg-n-amber-3 p-4 text-sm text-n-amber-12"
        >
          <span
            class="i-lucide-triangle-alert mt-0.5 size-5 shrink-0 text-n-amber-10"
          />
          <div>
            <strong>{{ copy.errorTitle }}</strong>
            <p class="mb-0 mt-1">{{ error }}</p>
          </div>
        </div>

        <section
          class="overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-sm"
        >
          <div
            class="grid gap-5 border-b border-n-weak bg-n-solid-1 p-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-center"
          >
            <div class="flex min-w-0 items-start gap-4">
              <span
                class="grid size-12 shrink-0 place-items-center rounded-2xl bg-n-brand/10 text-n-brand"
              >
                <span class="i-lucide-wallet-cards size-6" />
              </span>
              <div class="min-w-0">
                <p
                  class="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-n-brand"
                >
                  {{ copy.overview }}
                </p>
                <h2 class="text-xl font-semibold text-n-slate-12">
                  {{
                    hasSubscription
                      ? `Assinatura ${financial.subscription_id || ''}`
                      : copy.noSubscriptionShort
                  }}
                </h2>
                <p class="mb-0 mt-1 text-sm leading-6 text-n-slate-10">
                  {{ copy.overviewDescription }}
                </p>
              </div>
            </div>
            <span
              class="inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold"
              :class="
                hasFinancialLink
                  ? 'border-n-teal-7 bg-n-teal-3 text-n-teal-11'
                  : 'border-n-amber-7 bg-n-amber-3 text-n-amber-11'
              "
            >
              <span
                class="size-2 rounded-full"
                :class="hasFinancialLink ? 'bg-n-teal-9' : 'bg-n-amber-9'"
              />
              {{ hasFinancialLink ? copy.linked : copy.pendingSetup }}
            </span>
          </div>

          <div class="grid gap-px bg-n-weak sm:grid-cols-2 xl:grid-cols-4">
            <div class="bg-n-solid-2 p-4">
              <div class="mb-3 flex items-center justify-between gap-3">
                <span class="text-xs font-medium text-n-slate-10">
                  {{ copy.subscription }}
                </span>
                <span class="i-lucide-badge-check size-4 text-n-brand" />
              </div>
              <strong class="block truncate text-base text-n-slate-12">
                {{
                  hasSubscription
                    ? subscriptionStatusLabel(subscription.status)
                    : copy.noSubscriptionShort
                }}
              </strong>
            </div>
            <div class="bg-n-solid-2 p-4">
              <div class="mb-3 flex items-center justify-between gap-3">
                <span class="text-xs font-medium text-n-slate-10">
                  {{ copy.value }}
                </span>
                <span class="i-lucide-banknote size-4 text-n-teal-10" />
              </div>
              <strong class="block text-base text-n-slate-12">
                {{
                  hasSubscription
                    ? formatCurrency(subscription.value)
                    : copy.noChargeValue
                }}
              </strong>
            </div>
            <div class="bg-n-solid-2 p-4">
              <div class="mb-3 flex items-center justify-between gap-3">
                <span class="text-xs font-medium text-n-slate-10">
                  {{ copy.nextDueDate }}
                </span>
                <span class="i-lucide-calendar-clock size-4 text-n-amber-10" />
              </div>
              <strong class="block text-base text-n-slate-12">
                {{ formatDate(subscription.nextDueDate) }}
              </strong>
            </div>
            <div class="bg-n-solid-2 p-4">
              <div class="mb-3 flex items-center justify-between gap-3">
                <span class="text-xs font-medium text-n-slate-10">
                  {{ copy.accessExpiresAt }}
                </span>
                <span class="i-lucide-shield-check size-4 text-n-blue-10" />
              </div>
              <strong class="block text-base text-n-slate-12">
                {{
                  currentAccessExpiry
                    ? formatDateTime(currentAccessExpiry)
                    : copy.noExpiry
                }}
              </strong>
            </div>
          </div>
        </section>

        <div
          v-if="!hasFinancialLink && !error"
          class="flex items-start gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4 shadow-sm"
        >
          <span
            class="grid size-9 shrink-0 place-items-center rounded-xl bg-n-alpha-2 text-n-brand"
          >
            <span class="i-lucide-link-2-off size-4" />
          </span>
          <div>
            <p class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ copy.missingCompany }}
            </p>
            <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-10">
              {{ copy.missingCompanyDescription }}
            </p>
          </div>
        </div>

        <section
          v-if="hasSubscription"
          class="overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-sm"
        >
          <div
            class="flex flex-wrap items-start justify-between gap-4 border-b border-n-weak p-5"
          >
            <div>
              <h2 class="mb-1 text-base font-semibold text-n-slate-12">
                {{ copy.subscriptionDetails }}
              </h2>
              <p class="mb-0 text-sm text-n-slate-10">
                {{ copy.subscriptionDetailsDescription }}
              </p>
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <span
                class="inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold"
                :class="subscriptionStatusClass"
              >
                {{ subscriptionStatusLabel(subscription.status) }}
              </span>
              <button
                v-if="isSubscriptionActive && !withinTrialPeriod"
                type="button"
                class="inline-flex h-9 items-center gap-2 rounded-lg border border-n-ruby-7 bg-n-solid-1 px-3 text-xs font-semibold text-n-ruby-11 transition hover:bg-n-ruby-3"
                @click="openCancelModal('subscription')"
              >
                <span class="i-lucide-circle-x size-4" />
                {{ copy.cancelSubscription }}
              </button>
            </div>
          </div>

          <div class="grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-4">
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
              <span class="block text-xs text-n-slate-11">
                {{ copy.subscriptionId }}
              </span>
              <strong class="mt-1 block truncate text-sm text-n-slate-12">
                {{ financial.subscription_id || '-' }}
              </strong>
            </div>
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
              <span class="block text-xs text-n-slate-11">
                {{ copy.billingType }}
              </span>
              <strong class="mt-1 block text-sm text-n-slate-12">
                {{ billingTypeLabel(subscription.billingType) }}
              </strong>
            </div>
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
              <span class="block text-xs text-n-slate-11">
                {{ copy.cycle }}
              </span>
              <strong class="mt-1 block text-sm text-n-slate-12">
                {{ cycleLabel(subscription.cycle) }}
              </strong>
            </div>
            <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
              <span class="block text-xs text-n-slate-11">
                {{ copy.companyAccess }}
              </span>
              <strong class="mt-1 block text-sm text-n-slate-12">
                {{
                  currentAccessExpiry
                    ? formatDateTime(currentAccessExpiry)
                    : copy.noExpiry
                }}
              </strong>
            </div>
          </div>
        </section>

        <section
          class="overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-sm"
        >
          <div
            class="flex flex-wrap items-start justify-between gap-4 border-b border-n-weak p-5"
          >
            <div>
              <h2 class="mb-1 text-base font-semibold text-n-slate-12">
                {{ copy.invoices }}
              </h2>
              <p class="mb-0 text-sm text-n-slate-10">
                {{ payments.length }} {{ copy.invoiceCount }}
              </p>
            </div>
            <div class="flex flex-wrap gap-2">
              <span class="financial-counter text-n-teal-11">
                {{ paidPayments.length }} {{ copy.paidPayments }}
              </span>
              <span class="financial-counter text-n-amber-11">
                {{ openPayments.length }} {{ copy.openPayments }}
              </span>
              <span
                v-if="overduePayments.length"
                class="financial-counter text-n-ruby-11"
              >
                {{ overduePayments.length }} {{ copy.overduePayments }}
              </span>
            </div>
          </div>

          <div
            v-if="!payments.length"
            class="grid place-items-center gap-2 px-5 py-10 text-center"
          >
            <span
              class="grid size-11 place-items-center rounded-2xl bg-n-alpha-2 text-n-slate-10"
            >
              <span class="i-lucide-receipt-text size-5" />
            </span>
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              {{ copy.noPayments }}
            </p>
            <p class="mb-0 text-xs text-n-slate-10">
              {{ copy.futurePaymentsHint }}
            </p>
          </div>

          <div v-else>
            <div
              v-if="openPayments.length"
              class="flex flex-wrap items-center justify-between gap-3 border-b border-n-weak bg-n-amber-3/30 px-5 py-3"
            >
              <span class="text-xs font-medium text-n-amber-11">
                {{ copy.outstandingValue }}
              </span>
              <strong class="text-sm text-n-amber-12">
                {{ formatCurrency(outstandingValue) }}
              </strong>
            </div>
            <div class="overflow-x-auto px-5 pb-3">
              <table class="min-w-full divide-y divide-n-weak text-sm">
                <thead>
                  <tr
                    class="text-left text-xs font-semibold uppercase text-n-slate-11"
                  >
                    <th class="px-2 py-3 pr-4">
                      {{ copy.payment }}
                    </th>
                    <th class="px-2 py-3 pr-4">
                      {{ copy.status }}
                    </th>
                    <th class="px-2 py-3 pr-4">
                      {{ copy.value }}
                    </th>
                    <th class="px-2 py-3 pr-4">
                      {{ copy.dueDate }}
                    </th>
                    <th class="px-2 py-3 pr-4">
                      {{ copy.paymentDate }}
                    </th>
                    <th class="px-2 py-3 pr-4 text-right">
                      {{ copy.links }}
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak">
                  <tr v-for="payment in payments" :key="payment.id">
                    <td class="px-2 py-3 pr-4">
                      <strong class="block text-n-slate-12">
                        {{ payment.id }}
                      </strong>
                      <span class="text-n-slate-11">
                        {{ payment.description }}
                      </span>
                    </td>
                    <td class="px-2 py-3 pr-4">
                      <span
                        class="inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold"
                        :class="paymentStatusClass(payment.status)"
                      >
                        {{ statusLabel(payment.status) }}
                      </span>
                    </td>
                    <td class="px-2 py-3 pr-4 font-semibold text-n-slate-12">
                      {{ formatCurrency(payment.value) }}
                    </td>
                    <td class="px-2 py-3 pr-4 text-n-slate-12">
                      {{ formatDate(payment.dueDate) }}
                    </td>
                    <td class="px-2 py-3 pr-4 text-n-slate-12">
                      {{
                        formatDate(
                          payment.paymentDate || payment.clientPaymentDate
                        )
                      }}
                    </td>
                    <td class="px-2 py-3 text-right">
                      <div class="flex flex-wrap justify-end gap-2">
                        <button
                          v-if="['PENDING', 'OVERDUE'].includes(payment.status)"
                          type="button"
                          class="financial-link"
                          @click="openPaymentModal(payment)"
                        >
                          {{ copy.invoice }}
                        </button>
                        <span
                          v-else
                          class="text-xs font-medium text-n-slate-10"
                        >
                          {{ copy.paid }}
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section
          class="overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-sm"
        >
          <div
            class="flex flex-wrap items-start justify-between gap-4 border-b border-n-weak p-5"
          >
            <div>
              <h2 class="mb-1 text-base font-semibold text-n-slate-12">
                {{ copy.oneOffPurchases }}
              </h2>
              <p class="mb-0 text-sm text-n-slate-10">
                {{ copy.oneOffPurchasesDescription }}
              </p>
            </div>
            <div class="flex flex-wrap gap-2">
              <span class="financial-counter text-n-slate-11">
                {{ oneOffPurchases.length }} {{ copy.oneOffPurchaseCount }}
              </span>
              <span
                v-if="openOneOffPurchases.length"
                class="financial-counter text-n-amber-11"
              >
                {{ openOneOffPurchases.length }} {{ copy.openPayments }}
              </span>
            </div>
          </div>

          <div
            v-if="!oneOffPurchases.length"
            class="grid place-items-center gap-2 px-5 py-10 text-center"
          >
            <span
              class="grid size-11 place-items-center rounded-2xl bg-n-alpha-2 text-n-slate-10"
            >
              <span class="i-lucide-shopping-bag size-5" />
            </span>
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              {{ copy.noOneOffPurchases }}
            </p>
            <p class="mb-0 text-xs text-n-slate-10">
              {{ copy.noOneOffPurchasesHint }}
            </p>
          </div>

          <div v-else class="overflow-x-auto px-5 pb-3">
            <table class="min-w-full divide-y divide-n-weak text-sm">
              <thead>
                <tr
                  class="text-left text-xs font-semibold uppercase text-n-slate-11"
                >
                  <th class="px-2 py-3 pr-4">
                    {{ copy.oneOffProduct }}
                  </th>
                  <th class="px-2 py-3 pr-4">
                    {{ copy.status }}
                  </th>
                  <th class="px-2 py-3 pr-4">
                    {{ copy.credits }}
                  </th>
                  <th class="px-2 py-3 pr-4">
                    {{ copy.value }}
                  </th>
                  <th class="px-2 py-3 pr-4">
                    {{ copy.purchaseDate }}
                  </th>
                  <th class="px-2 py-3 pr-4">
                    {{ copy.dueDate }}
                  </th>
                  <th class="px-2 py-3 text-right">
                    {{ copy.links }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr
                  v-for="purchase in oneOffPurchases"
                  :key="purchase.public_id"
                >
                  <td class="px-2 py-3 pr-4">
                    <strong class="block text-n-slate-12">
                      {{ purchase.product_name }}
                    </strong>
                    <span class="text-xs text-n-slate-11">
                      {{ purchase.units }} {{ copy.packages }}
                    </span>
                  </td>
                  <td class="px-2 py-3 pr-4">
                    <span
                      class="inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold"
                      :class="paymentStatusClass(purchase.status.toUpperCase())"
                    >
                      {{ statusLabel(purchase.status.toUpperCase()) }}
                    </span>
                  </td>
                  <td class="px-2 py-3 pr-4 font-semibold text-n-slate-12">
                    {{ purchase.credits.toLocaleString('pt-BR') }}
                  </td>
                  <td class="px-2 py-3 pr-4 font-semibold text-n-slate-12">
                    {{ formatCurrency(purchase.amount) }}
                  </td>
                  <td class="px-2 py-3 pr-4 text-n-slate-12">
                    {{ formatDateTime(purchase.created_at) }}
                  </td>
                  <td class="px-2 py-3 pr-4 text-n-slate-12">
                    {{ formatDate(purchase.due_date) }}
                  </td>
                  <td class="px-2 py-3 text-right">
                    <button
                      v-if="
                        ['pending', 'overdue'].includes(purchase.status) &&
                        purchase.payment_id
                      "
                      type="button"
                      class="financial-link"
                      @click="openOneOffPaymentModal(purchase)"
                    >
                      {{ copy.invoice }}
                    </button>
                    <span v-else class="text-xs font-medium text-n-slate-10">
                      {{
                        purchase.status === 'paid'
                          ? copy.paid
                          : copy.notAvailable
                      }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section
          v-if="withinTrialPeriod"
          class="overflow-hidden rounded-2xl border border-n-ruby-7/60 bg-n-ruby-3/30"
        >
          <div class="flex flex-wrap items-center justify-between gap-5 p-5">
            <div class="flex max-w-3xl items-start gap-3">
              <span
                class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-ruby-4 text-n-ruby-11"
              >
                <span class="i-lucide-shield-alert size-5" />
              </span>
              <div>
                <h2 class="mb-1 text-base font-semibold text-n-ruby-12">
                  {{ copy.trialCancellationTitle }}
                </h2>
                <p class="mb-2 text-sm leading-6 text-n-ruby-11">
                  {{ copy.trialCancellationDescription }}
                </p>
                <p class="mb-0 text-xs font-medium text-n-ruby-11">
                  {{ copy.trialEndsAt }}
                  {{ formatDateTime(financial.trial_ends_at) }}
                </p>
              </div>
            </div>
            <button
              type="button"
              class="inline-flex h-10 shrink-0 items-center gap-2 rounded-lg border border-n-ruby-8 bg-n-ruby-9 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-n-ruby-10"
              @click="openCancelModal('account')"
            >
              <span class="i-lucide-user-x size-4" />
              {{ copy.cancelAccount }}
            </button>
          </div>
        </section>
      </section>

      <!-- Talkvia payment modal -->
      <Teleport to="body">
        <Transition name="modal-fade">
          <div
            v-if="showPaymentModal"
            class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
            @click.self="closePaymentModal"
          >
            <div
              class="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-2xl"
            >
              <header
                class="flex shrink-0 items-start gap-3 border-b border-n-weak px-5 py-4"
              >
                <span
                  class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
                >
                  <span class="i-lucide-wallet-cards size-5" />
                </span>
                <div class="min-w-0 flex-1">
                  <h3 class="mb-1 text-base font-semibold text-n-slate-12">
                    {{ copy.checkoutTitle }}
                  </h3>
                  <p class="mb-0 text-sm text-n-slate-10">
                    {{ copy.checkoutDescription }}
                  </p>
                </div>
                <button
                  type="button"
                  class="grid size-8 shrink-0 place-items-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
                  :disabled="isPayingCard"
                  @click="closePaymentModal"
                >
                  <span class="i-lucide-x size-4" />
                </button>
              </header>

              <div class="min-h-0 flex-1 overflow-y-auto p-5">
                <div
                  class="mb-5 flex items-center justify-between gap-4 rounded-xl border border-n-weak bg-n-solid-2 p-4"
                >
                  <div>
                    <span class="block text-xs text-n-slate-10">
                      {{ copy.payment }}
                    </span>
                    <strong class="mt-1 block text-sm text-n-slate-12">
                      {{ selectedPayment?.description || selectedPayment?.id }}
                    </strong>
                  </div>
                  <strong class="shrink-0 text-lg text-n-slate-12">
                    {{ formatCurrency(selectedPayment?.value) }}
                  </strong>
                </div>

                <div
                  class="mb-5 grid grid-cols-2 gap-2 rounded-xl bg-n-alpha-2 p-1"
                >
                  <button
                    type="button"
                    class="h-10 rounded-lg text-sm font-semibold transition"
                    :class="
                      paymentMethod === 'pix'
                        ? 'bg-n-solid-1 text-n-brand shadow-sm'
                        : 'text-n-slate-11 hover:text-n-slate-12'
                    "
                    @click="selectPaymentMethod('pix')"
                  >
                    {{ copy.pix }}
                  </button>
                  <button
                    type="button"
                    class="h-10 rounded-lg text-sm font-semibold transition"
                    :class="
                      paymentMethod === 'card'
                        ? 'bg-n-solid-1 text-n-brand shadow-sm'
                        : 'text-n-slate-11 hover:text-n-slate-12'
                    "
                    @click="selectPaymentMethod('card')"
                  >
                    {{ copy.card }}
                  </button>
                </div>

                <div
                  v-if="checkoutError"
                  class="mb-4 rounded-lg border border-n-ruby-6 bg-n-ruby-3 p-3 text-sm text-n-ruby-12"
                >
                  {{ checkoutError }}
                </div>
                <div
                  v-if="checkoutSuccess"
                  class="mb-4 rounded-lg border border-n-teal-6 bg-n-teal-3 p-3 text-sm text-n-teal-12"
                >
                  {{ checkoutSuccess }}
                </div>
                <div
                  v-if="checkoutWarning"
                  class="mb-4 rounded-lg border border-n-amber-6 bg-n-amber-3 p-3 text-sm text-n-amber-12"
                >
                  {{ checkoutWarning }}
                </div>

                <section v-if="paymentMethod === 'pix'" class="grid gap-4">
                  <div
                    v-if="isLoadingPix"
                    class="grid min-h-48 place-items-center text-sm text-n-slate-10"
                  >
                    {{ copy.pixLoading }}
                  </div>
                  <template v-else-if="pixData?.payload">
                    <img
                      v-if="pixData.encoded_image"
                      :src="`data:image/png;base64,${pixData.encoded_image}`"
                      alt="QR Code Pix"
                      class="mx-auto size-52 rounded-xl bg-white p-3"
                    />
                    <label
                      class="grid gap-2 text-sm font-medium text-n-slate-12"
                    >
                      {{ copy.pixCopyPaste }}
                      <textarea
                        :value="pixData.payload"
                        readonly
                        rows="4"
                        class="w-full resize-none rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 font-mono text-xs text-n-slate-12 outline-none"
                      />
                    </label>
                    <p
                      v-if="pixData.expiration_date"
                      class="mb-0 text-xs text-n-slate-10"
                    >
                      {{
                        `${copy.pixExpires}: ${formatDateTime(pixData.expiration_date)}`
                      }}
                    </p>
                  </template>
                </section>

                <form
                  v-else
                  id="talkvia-card-payment-form"
                  class="grid gap-5"
                  @submit.prevent="payWithCard"
                >
                  <div class="grid gap-3 sm:grid-cols-2">
                    <label class="grid gap-2 text-sm font-medium sm:col-span-2">
                      {{ copy.cardHolderName }}
                      <input
                        v-model.trim="cardForm.holder_name"
                        required
                        autocomplete="cc-name"
                        class="checkout-input"
                      />
                    </label>
                    <label class="grid gap-2 text-sm font-medium sm:col-span-2">
                      {{ copy.cardNumber }}
                      <input
                        v-model="cardForm.card_number"
                        required
                        inputmode="numeric"
                        autocomplete="cc-number"
                        maxlength="23"
                        minlength="13"
                        class="checkout-input"
                      />
                    </label>
                    <label class="grid gap-2 text-sm font-medium">
                      {{ copy.cardExpiryMonth }}
                      <input
                        v-model="cardForm.expiry_month"
                        required
                        inputmode="numeric"
                        autocomplete="cc-exp-month"
                        maxlength="2"
                        class="checkout-input"
                      />
                    </label>
                    <label class="grid gap-2 text-sm font-medium">
                      {{ copy.cardExpiryYear }}
                      <input
                        v-model="cardForm.expiry_year"
                        required
                        inputmode="numeric"
                        autocomplete="cc-exp-year"
                        maxlength="4"
                        minlength="4"
                        class="checkout-input"
                      />
                    </label>
                    <label class="grid gap-2 text-sm font-medium">
                      {{ copy.cardCcv }}
                      <input
                        v-model="cardForm.ccv"
                        required
                        type="password"
                        inputmode="numeric"
                        autocomplete="cc-csc"
                        maxlength="4"
                        class="checkout-input"
                      />
                    </label>
                  </div>

                  <div
                    class="grid gap-3 border-t border-n-weak pt-5 sm:grid-cols-2"
                  >
                    <label class="grid gap-2 text-sm font-medium sm:col-span-2">
                      {{ copy.holderName }}
                      <input
                        v-model.trim="cardForm.name"
                        required
                        class="checkout-input"
                      />
                    </label>
                    <label class="grid gap-2 text-sm font-medium">
                      {{ copy.holderDocument }}
                      <input
                        v-model="cardForm.cpf_cnpj"
                        required
                        inputmode="numeric"
                        maxlength="18"
                        class="checkout-input"
                      />
                    </label>
                    <label class="grid gap-2 text-sm font-medium">
                      {{ copy.holderEmail }}
                      <input
                        v-model.trim="cardForm.email"
                        required
                        type="email"
                        autocomplete="email"
                        class="checkout-input"
                      />
                    </label>
                    <label class="grid gap-2 text-sm font-medium">
                      {{ copy.holderPostalCode }}
                      <input
                        v-model="cardForm.postal_code"
                        required
                        inputmode="numeric"
                        autocomplete="postal-code"
                        maxlength="9"
                        class="checkout-input"
                      />
                    </label>
                    <label class="grid gap-2 text-sm font-medium">
                      {{ copy.holderAddressNumber }}
                      <input
                        v-model.trim="cardForm.address_number"
                        required
                        class="checkout-input"
                      />
                    </label>
                    <label class="grid gap-2 text-sm font-medium">
                      {{ copy.holderAddressComplement }}
                      <input
                        v-model.trim="cardForm.address_complement"
                        class="checkout-input"
                      />
                    </label>
                    <label class="grid gap-2 text-sm font-medium">
                      {{ copy.holderPhone }}
                      <input
                        v-model="cardForm.phone"
                        inputmode="tel"
                        autocomplete="tel"
                        class="checkout-input"
                      />
                    </label>
                  </div>

                  <label
                    v-if="!selectedPayment?.oneOff"
                    class="flex cursor-pointer items-start gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-3 text-sm text-n-slate-12"
                  >
                    <input
                      v-model="cardForm.use_for_subscription"
                      type="checkbox"
                      class="mt-0.5 size-4 accent-[rgb(var(--color-n-brand))]"
                    />
                    <span>{{ copy.useForSubscription }}</span>
                  </label>
                  <p class="mb-0 text-xs leading-5 text-n-slate-10">
                    {{ copy.cardSecurity }}
                  </p>
                </form>
              </div>

              <footer
                class="flex shrink-0 justify-end gap-2 border-t border-n-weak bg-n-solid-1 px-5 py-4"
              >
                <button
                  type="button"
                  class="h-10 rounded-lg border border-n-weak bg-n-solid-2 px-4 text-sm font-semibold text-n-slate-12 hover:bg-n-alpha-2"
                  :disabled="isPayingCard"
                  @click="closePaymentModal"
                >
                  {{ copy.close }}
                </button>
                <button
                  v-if="paymentMethod === 'pix' && pixData?.payload"
                  type="button"
                  class="h-10 rounded-lg bg-n-brand px-4 text-sm font-semibold text-white hover:brightness-95"
                  @click="copyPixCode"
                >
                  {{ copy.copyPix }}
                </button>
                <button
                  v-if="paymentMethod === 'card' && selectedPaymentIsOpen"
                  type="submit"
                  form="talkvia-card-payment-form"
                  class="h-10 rounded-lg bg-n-brand px-4 text-sm font-semibold text-white hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="isPayingCard"
                >
                  {{ isPayingCard ? copy.processingCard : copy.payCard }}
                </button>
              </footer>
            </div>
          </div>
        </Transition>
      </Teleport>

      <!-- Cancel subscription modal -->
      <Teleport to="body">
        <Transition name="modal-fade">
          <div
            v-if="showCancelModal"
            class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            @click.self="closeCancelModal"
          >
            <div
              class="relative w-full max-w-lg overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-2xl"
            >
              <div class="flex items-start gap-3 border-b border-n-weak p-5">
                <span
                  class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-ruby-3 text-n-ruby-11"
                >
                  <span class="i-lucide-triangle-alert size-5" />
                </span>
                <div class="min-w-0 flex-1">
                  <h3 class="mb-1 text-base font-semibold text-n-slate-12">
                    {{ cancelModalTitle }}
                  </h3>
                  <p class="mb-0 text-sm leading-6 text-n-slate-11">
                    {{ cancelModalDescription }}
                  </p>
                </div>
                <button
                  type="button"
                  class="grid size-8 shrink-0 place-items-center rounded-lg text-n-slate-10 transition hover:bg-n-alpha-2 hover:text-n-slate-12"
                  @click="closeCancelModal"
                >
                  <span class="i-lucide-x size-4" />
                </button>
              </div>

              <div class="p-5">
                <div
                  v-if="cancelSuccess"
                  class="mb-4 rounded-lg border border-n-teal-6 bg-n-teal-3 p-3 text-sm text-n-teal-12"
                >
                  {{ cancelSuccessMessage }}
                </div>

                <div
                  v-if="cancelError"
                  class="mb-4 rounded-lg border border-n-ruby-6 bg-n-ruby-3 p-3 text-sm text-n-ruby-12"
                >
                  {{ cancelError }}
                </div>

                <div v-if="!cancelSuccess" class="grid gap-3">
                  <p class="text-xs font-semibold uppercase text-n-slate-11">
                    {{ copy.cancelReasonLabel }}
                  </p>
                  <div class="grid gap-2">
                    <label
                      v-for="reason in CANCEL_REASONS"
                      :key="reason"
                      class="flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors"
                      :class="
                        selectedReason === reason
                          ? 'border-n-ruby-7 bg-n-ruby-3 text-n-ruby-12'
                          : 'border-n-weak bg-n-solid-2 text-n-slate-12 hover:border-n-strong'
                      "
                    >
                      <input
                        v-model="selectedReason"
                        type="radio"
                        :value="reason"
                        class="accent-[rgb(var(--color-n-ruby-9))]"
                      />
                      {{ reason }}
                    </label>
                  </div>

                  <textarea
                    v-if="isOtherReason"
                    v-model="otherReason"
                    :placeholder="copy.cancelReasonPlaceholder"
                    rows="3"
                    class="mt-1 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 placeholder-n-slate-9 outline-none transition focus:border-n-ruby-8"
                  />

                  <div
                    class="mt-2 flex flex-col-reverse justify-end gap-2 sm:flex-row"
                  >
                    <button
                      type="button"
                      class="inline-flex h-10 items-center justify-center rounded-lg border border-n-weak bg-n-solid-2 px-4 text-sm font-semibold text-n-slate-12 transition hover:bg-n-alpha-2 disabled:opacity-50"
                      :disabled="isCancelling"
                      @click="closeCancelModal"
                    >
                      {{ copy.cancelDismiss }}
                    </button>
                    <button
                      type="button"
                      class="inline-flex h-10 items-center justify-center rounded-lg bg-n-ruby-9 px-4 text-sm font-semibold text-white transition hover:bg-n-ruby-10 disabled:cursor-not-allowed disabled:opacity-50"
                      :disabled="!canConfirmCancel || isCancelling"
                      @click="confirmCancel"
                    >
                      <span v-if="isCancelling">{{ copy.cancelling }}</span>
                      <span v-else>{{ cancelConfirmLabel }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>
    </template>
  </SettingsLayout>
</template>

<style scoped>
.financial-counter {
  @apply inline-flex items-center rounded-full border border-n-weak bg-n-alpha-2 px-2.5 py-1 text-[11px] font-semibold;
}

.financial-link {
  @apply inline-flex h-8 items-center rounded-lg border border-n-weak bg-n-solid-1 px-2.5 text-xs font-semibold text-n-brand transition hover:border-n-brand/40 hover:bg-n-brand/5;
}

.checkout-input {
  @apply h-10 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none transition focus:border-n-brand focus:ring-1 focus:ring-n-brand;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
